import type { GaugeChartConfig } from './types';

// Fraction of the container height the arc radius takes when there is width to spare. Matches
// what the legacy '140%' radius resolved to, since ECharts sizes a gauge radius percentage
// against min(width, height) / 2 (see echarts/lib/chart/gauge/GaugeView.js parsePosition).
export const GAUGE_RADIUS_TO_HEIGHT = 0.7;

// Legacy radius, used until the container has been measured.
const FALLBACK_RADIUS = '140%';

type GaugeRadiusArgs = {
  containerWidth?: number;
  containerHeight?: number;
  trackWidth: number;
};

/**
 * Largest radius (px) that keeps the whole arc inside the container, or undefined when the
 * container has not been measured yet.
 *
 * A percentage radius alone cannot do this: ECharts resolves it against min(width, height), so a
 * gauge in an elastic-width container both rescaled with the window and overflowed sideways once
 * that container got narrow (SIMPL-34739).
 */
export const resolveGaugeRadius = ({ containerWidth, containerHeight, trackWidth }: GaugeRadiusArgs) => {
  if (!containerWidth || !containerHeight) return undefined;
  // The arc reaches trackWidth past the radius horizontally (half the stroke plus the roundCap at
  // the 180/0 deg ends), and that has to fit in half the container.
  const maxByWidth = containerWidth / 2 - trackWidth;
  return Math.max(0, Math.min(GAUGE_RADIUS_TO_HEIGHT * containerHeight, maxByWidth));
};

export const buildGaugeSeries = (value: number, config: GaugeChartConfig) => {
  const {
    min,
    max,
    segments,
    pointerColor,
    startAngle = 180,
    endAngle = 0,
    trackWidth = 20,
    centerY = '90%',
    containerWidth,
    containerHeight,
  } = config;

  const range = max - min;
  // Insert thin transparent bands between segments so each color reads as a
  // separate rounded pill (matches the AMCharts axisFill cornerRadius + gaps).
  const segmentGap = 0.012;
  const axisLineColors: [number, string][] = [];
  segments.forEach((seg, i) => {
    const end = (seg.max - min) / range;
    const isLast = i === segments.length - 1;
    axisLineColors.push([isLast ? end : Math.max(0, end - segmentGap), seg.color]);
    if (!isLast) axisLineColors.push([end, 'transparent']);
  });

  const color = pointerColor ?? '#000000';

  return {
    type: 'gauge' as const,
    startAngle,
    endAngle,
    min,
    max,
    radius: resolveGaugeRadius({ containerWidth, containerHeight, trackWidth }) ?? FALLBACK_RADIUS,
    center: ['50%', centerY],
    axisLine: {
      roundCap: true,
      lineStyle: {
        width: trackWidth,
        color: axisLineColors,
      },
    },
    pointer: {
      show: true,
      // path bounding box: bottom edge = offsetCenter = tip (toward center); top = base (outward)
      // M0,7 = tip at path-bottom; L-8,-7 L8,-7 = base at path-top
      icon: 'path://M0,7L-8,-7L8,-7Z',
      width: 14,
      length: 12,
      offsetCenter: [0, '-103%'],
      itemStyle: { color },
    },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
    detail: { show: false },
    title: { show: false },
    data: [{ value, name: '' }],
  };
};
